const DEVELOPMENT = 'DEVELOPMENT';
const PRODUCTION = 'PRODUCTION';

/**
 * Checks if the current environment is set to "DEVELOPMENT".
 *
 * This function reads the `REACT_APP_ENVIRONMENT` environment variable
 * and returns `true` if it matches the "DEVELOPMENT" value, indicating that
 * the application is running in a development environment. If `REACT_APP_ENVIRONMENT`
 * is set to any other value or is undefined, the function returns `false`.
 *
 * @returns {boolean} - `true` if the environment is "DEVELOPMENT", otherwise `false`.
 */
export const isDevelopment = (): boolean => process.env.REACT_APP_ENVIRONMENT === DEVELOPMENT;

/**
 * Checks if the current environment is set to "PRODUCTION".
 *
 * This function reads the `REACT_APP_ENVIRONMENT` environment variable
 * and returns `true` if it matches the "PRODUCTION" value, indicating that
 * the application is running in a production environment. If `REACT_APP_ENVIRONMENT`
 * is set to any other value or is undefined, the function returns `false`.
 *
 * @returns {boolean} - `true` if the environment is "PRODUCTION", otherwise `false`.
 */
export const isProduction = (): boolean => process.env.REACT_APP_ENVIRONMENT === PRODUCTION;

/**
 * Forces a re-render of the component by delaying the resolution of a promise.
 *
 * This function introduces a minimal delay using `setTimeout`, effectively pushing the resolution of the promise to the next event loop cycle.
 * This can be useful in scenarios where a re-render needs to be triggered after state changes.
 */
export const forceRender = () =>
  new Promise((resolve) => {
    setTimeout(resolve, 0);
  });

/**
 * Toggles the `prevent-scroll` class on the document body to lock or unlock page scrolling.
 *
 * @param {boolean} enabled - When `true`, adds the class to prevent scroll; when `false`, removes it.
 */
export const togglePreventScroll = (enabled: boolean): void => {
  document.body.classList.toggle('prevent-scroll', enabled);
};
